import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GET_PLANNER_ITEM_THUMBNAIL_QUERY } from './get-planner-item-thumbnail.query';
import {
    GetPlannerItemThumbnailQuery,
    GetPlannerItemThumbnailQueryVariables,
} from './__graphqlTypes/GetPlannerItemThumbnailQuery';

type Thumbnail = {
    title: string;
    url: string;
};

type UsePlannerItemThumbnailReturn = {
    loading: boolean;
    thumbnail: Thumbnail | null;
};

export function usePlannerItemThumbnail(plannerId: string): UsePlannerItemThumbnailReturn {
    const { data, loading } = useQuery<GetPlannerItemThumbnailQuery, GetPlannerItemThumbnailQueryVariables>(
        GET_PLANNER_ITEM_THUMBNAIL_QUERY,
        {
            variables: {
                plannerId,
            },
        },
    );
    const thumbnail = useMemo<Thumbnail | null>(() => {
        if (!data || data.plannerEvent.__typename !== 'PlannerEvent') {
            return null;
        }
        // Prioritise the published artifact thumbnail first
        if (data.plannerEvent.publishedArtifact?.thumbnails?.small.url) {
            return { title: 'Published', url: data.plannerEvent.publishedArtifact.thumbnails.small.url };
        }

        // Otherwise we use the buildable template or builder template thumbnail
        if (data.plannerEvent.suggestedTemplate) {
            const thumbnailUrl =
                data.plannerEvent.suggestedTemplate?.__typename === 'BuilderTemplate'
                    ? data.plannerEvent.suggestedTemplate.thumbnailUrl
                    : data.plannerEvent.suggestedTemplate.currentVersion.thumbnailUrl;
            if (thumbnailUrl) {
                return {
                    title: 'Suggested template',
                    url: thumbnailUrl,
                };
            }
        }
        return null;
    }, [data]);

    return { loading, thumbnail };
}
